import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, FormControl, InputLabel, Select, MenuItem,
  FormHelperText, CircularProgress, InputAdornment, Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMaintenanceTypes } from "../../../hooks/maintenance/useMaintenanceTypes";

const schema = yup.object().shape({
  tipmanid: yup.number().required("El tipo de mantenimiento es obligatorio.")
    .positive("Seleccione un tipo de mantenimiento válido."),
  manfecini: yup.date().required("La fecha/hora de inicio es obligatoria.")
    .typeError("Formato inválido."),
  manfecfin: yup.date().nullable().typeError("Formato inválido.")
    .test("is-after-start", "La fecha fin no puede ser anterior al inicio.",
      function (value) {
        const { manfecini } = this.parent;
        return !manfecini || !value || value >= manfecini;
      }),
  manest: yup.string().required("El estado es obligatorio.")
    .oneOf(['CREADO', 'INICIADO', 'EN_PROCESO', 'FINALIZADO', 'CANCELADO']),
  mankm: yup.number().nullable().min(0, "No puede ser negativo.").typeError("Debe ser número."),
  manobs: yup.string().nullable().max(255, "Máximo 255 caracteres."),
  mantiefuehor: yup.number().nullable().min(0, "No puede ser negativo.")
    .integer("Debe ser entero.").typeError("Debe ser número."),
  costototal: yup.number().nullable().min(0, "No puede ser negativo.").typeError("Debe ser número."),
});

const VehicleMaintenanceForm = ({ open, handleClose, onSubmit, defaultValues }) => {
  const { maintenanceTypes, isLoading, error } = useMaintenanceTypes();

  const {
    handleSubmit, control, reset, formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      // vehid viene desde el Dashboard (contexto del vehículo)
      tipmanid: defaultValues.tipmanid || "",
      manfecini: defaultValues.manfecini ? new Date(defaultValues.manfecini) : null,
      manfecfin: defaultValues.manfecfin ? new Date(defaultValues.manfecfin) : null,
      manest: defaultValues.manest || "CREADO",
      mankm: defaultValues.mankm ?? null,
      manobs: defaultValues.manobs ?? "",
      mantiefuehor: defaultValues.mantiefuehor ?? null,
      costototal: defaultValues.costototal ?? null,
    },
  });

  useEffect(() => {
    reset({
      tipmanid: defaultValues.tipmanid || "",
      manfecini: defaultValues.manfecini ? new Date(defaultValues.manfecini) : null,
      manfecfin: defaultValues.manfecfin ? new Date(defaultValues.manfecfin) : null,
      manest: defaultValues.manest || "CREADO",
      mankm: defaultValues.mankm ?? null,
      manobs: defaultValues.manobs ?? "",
      mantiefuehor: defaultValues.mantiefuehor ?? null,
      costototal: defaultValues.costototal ?? null,
    });
  }, [defaultValues, reset]);

  const handleFormSubmit = (data) => {
    const payload = {
      ...data,
      vehid: defaultValues.vehid, // <-- clave: enviar el vehículo
      mankm: data.mankm !== null && data.mankm !== '' ? Number(data.mankm) : null,
      mantiefuehor: data.mantiefuehor !== null && data.mantiefuehor !== '' ? Number(data.mantiefuehor) : null,
      costototal: data.costototal !== null && data.costototal !== '' ? Number(data.costototal) : null,
    };
    onSubmit(payload);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>{defaultValues.manid ? "Editar Mantenimiento" : "Crear Mantenimiento"}</DialogTitle>
        <DialogContent dividers>
          <Box component="form" noValidate sx={{ mt: 1 }}>
            {/* Contexto: Vehículo (solo lectura) */}
            <TextField
              margin="dense"
              label="Vehículo"
              value={`${defaultValues.vehiculomatricula || 'N/A'} (ID: ${defaultValues.vehid ?? 'N/A'})`}
              fullWidth
              variant="outlined"
              disabled
              sx={{ mb: 2 }}
            />

            {/* Tipo de mantenimiento */}
            {isLoading ? (
              <CircularProgress size={24} sx={{ mb: 2 }} />
            ) : error ? (
              <Typography color="error" sx={{ mb: 2 }}>
                Error al cargar tipos de mantenimiento: {error.message}
              </Typography>
            ) : (
              <Controller
                name="tipmanid"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth margin="dense" error={!!errors.tipmanid} sx={{ mb: 2 }}>
                    <InputLabel id="tip-man-id-label">Tipo de Mantenimiento</InputLabel>
                    <Select
                      labelId="tip-man-id-label"
                      label="Tipo de Mantenimiento"
                      {...field}
                      value={field.value || ""}
                    >
                      <MenuItem value=""><em>Seleccione…</em></MenuItem>
                      {maintenanceTypes.map((t) => (
                        <MenuItem key={t.tipmanid} value={t.tipmanid}>{t.tipmannom}</MenuItem>
                      ))}
                    </Select>
                    {errors.tipmanid && <FormHelperText>{errors.tipmanid.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            )}

            {/* Fechas */}
            <Controller
              name="manfecini"
              control={control}
              render={({ field: { onChange, value, ...field } }) => (
                <DateTimePicker
                  {...field}
                  label="Fecha y Hora de Inicio"
                  value={value}
                  onChange={(v) => onChange(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      fullWidth
                      error={!!errors.manfecini}
                      helperText={errors.manfecini?.message}
                    />
                  )}
                />
              )}
            />
            <Controller
              name="manfecfin"
              control={control}
              render={({ field: { onChange, value, ...field } }) => (
                <DateTimePicker
                  {...field}
                  label="Fecha y Hora de Fin (Opcional)"
                  value={value}
                  onChange={(v) => onChange(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      fullWidth
                      error={!!errors.manfecfin}
                      helperText={errors.manfecfin?.message}
                    />
                  )}
                />
              )}
            />

            {/* Estado */}
            {/*
            <Controller
              name="manest"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth margin="dense" error={!!errors.manest}>
                  <InputLabel id="man-est-label">Estado</InputLabel>
                  <Select labelId="man-est-label" label="Estado" {...field}>
                    <MenuItem value="CREADO">CREADO</MenuItem>
                    <MenuItem value="INICIADO">INICIADO</MenuItem>
                    <MenuItem value="EN_PROCESO">EN_PROCESO</MenuItem>
                    <MenuItem value="FINALIZADO">FINALIZADO</MenuItem>
                    <MenuItem value="CANCELADO">CANCELADO</MenuItem>
                  </Select>
                  {errors.manest && <FormHelperText>{errors.manest.message}</FormHelperText>}
                </FormControl>
              )}
            />
            */}

            {/* Números */}
            <Controller
              name="mankm"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="dense"
                  label="Kilometraje (Opcional)"
                  type="number"
                  fullWidth
                  error={!!errors.mankm}
                  helperText={errors.mankm?.message}
                  inputProps={{ min: 0 }}
                />
              )}
            />
            <Controller
              name="mantiefuehor"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="dense"
                  label="Tiempo Fuera de Horas (Opcional)"
                  type="number"
                  fullWidth
                  error={!!errors.mantiefuehor}
                  helperText={errors.mantiefuehor?.message}
                  inputProps={{ min: 0 }}
                />
              )}
            />
            {/*
            <Controller
              name="costototal"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="dense"
                  label="Costo Total (Opcional)"
                  type="number"
                  fullWidth
                  error={!!errors.costototal}
                  helperText={errors.costototal?.message}
                  inputProps={{ step: "0.01", min: 0 }}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
              )}
            />
            */}

            {/* Observaciones */}
            <Controller
              name="manobs"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="dense"
                  label="Observaciones (Opcional)"
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.manobs}
                  helperText={errors.manobs?.message}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">Cancelar</Button>
          <Button onClick={handleSubmit(handleFormSubmit)} variant="contained" color="primary">
            {defaultValues.manid ? "Guardar Cambios" : "Crear Mantenimiento"}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default VehicleMaintenanceForm;
